import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { getCurrentWeekDates, getProjectDetails } from '../../utils/common';
import { usePlan } from '../../context/WorkPlanContext';
import WorkPlanDialog from './WorkPlanDialog';
import ConfirmDeleteDialog from '../../components/ConfirmDialog';

const WorkingPlanManager = () => {
  const { plans = [], removePlan, fetchPlans } = usePlan();
  const [openDialog, setOpenDialog] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [deletePlanId, setDeletePlanId] = useState(null);

  // Main filter state
  const [filter, setFilter] = useState(getCurrentWeekDates());

  // Separate state for filter inputs
  const [filterInputs, setFilterInputs] = useState({
    start_day: getCurrentWeekDates().start_day || '',
    end_day: getCurrentWeekDates().end_day || '',
  });

  const [loading, setLoading] = useState(false); // Optional: Loading state

  useEffect(() => {
    handleGetWorkPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleGetWorkPlans = async () => {
    setLoading(true);
    try {
      await fetchPlans(filter);
    } catch (error) {
      console.error('Error fetching plans:', error);
      // Optional: Handle error state
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddDialog = () => {
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (plan) => {
    setCurrentPlan(plan);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDeletePlan = async (id) => {
    setDeletePlanId(id);
  };

  // Handle filter input changes
  const handleFilterInputChange = (e) => {
    const { name, value } = e.target;
    setFilterInputs({ ...filterInputs, [name]: value });
  };

  // Apply filter by updating the main filter state
  const applyFilter = () => {
    if (
      filterInputs.start_day &&
      filterInputs.end_day &&
      new Date(filterInputs.end_day) < new Date(filterInputs.start_day)
    ) {
      alert('End Date cannot be before Start Date.');
      return;
    }
    setFilter({
      start_day: filterInputs.start_day,
      end_day: filterInputs.end_day,
    });
  };

  // Reset filter to default values
  const resetFilter = () => {
    const defaultFilter = getCurrentWeekDates();
    setFilterInputs({
      start_day: defaultFilter.start_day || '',
      end_day: defaultFilter.end_day || '',
    });
    setFilter(defaultFilter);
  };

  return (
    <Box sx={{ padding: 4 }}>
      {/* Toolbar Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}>
        {/* Left Side: Add Button */}
        <Button
          variant="contained"
          color="primary"
          aria-hidden="true"
          onClick={handleOpenAddDialog}>
          Add Working Plan
        </Button>

        {/* Right Side: Filter Options */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}>
          <FormControl variant="outlined" size="small">
            <TextField
              label="Start Date"
              type="date"
              name="start_day"
              value={filterInputs.start_day}
              onChange={handleFilterInputChange}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
            />
          </FormControl>
          <FormControl variant="outlined" size="small">
            <TextField
              label="End Date"
              type="date"
              name="end_day"
              value={filterInputs.end_day}
              onChange={handleFilterInputChange}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
            />
          </FormControl>
          <Button variant="contained" color="primary" onClick={applyFilter}>
            Apply Filter
          </Button>
          <Button variant="outlined" color="secondary" onClick={resetFilter}>
            Reset Filter
          </Button>
        </Box>
      </Box>

      {/* Optional: Loading Indicator */}
      {loading ? (
        <Typography align="center" sx={{ padding: 2 }}>
          Loading...
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Project Name</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>Content</TableCell>
                <TableCell>Start Day</TableCell>
                <TableCell>End Day</TableCell>
                <TableCell>Work Time Per Day (Hours)</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.length > 0 ? (
                plans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell>
                      {getProjectDetails(plan.project)?.projectName}
                    </TableCell>
                    <TableCell>
                      {getProjectDetails(plan.project)?.platformType.join(', ')}
                    </TableCell>
                    <TableCell>{plan.content}</TableCell>
                    <TableCell>{plan.start_day}</TableCell>
                    <TableCell>{plan.end_day}</TableCell>
                    <TableCell>{plan.work_time_per_day}</TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenEditDialog(plan)}>
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="secondary"
                        onClick={() => handleDeletePlan(plan.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No working plans available.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <WorkPlanDialog
        isOpen={openDialog}
        onClose={handleCloseDialog}
        selectedWorkLogged={currentPlan}
      />
      <ConfirmDeleteDialog
        isOpen={!!deletePlanId}
        confirmText="Are you sure you want to delete this plan?"
        onClose={() => setDeletePlanId(null)}
        onConfirm={() => {
          removePlan(deletePlanId);
          setDeletePlanId(null);
        }}
      />
    </Box>
  );
};

export default WorkingPlanManager;
